'use client';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import {
    createCompanySearchTermQueryOptions,
    createProjectSearchOptions
} from '../../../../api/helpers';
import { fetchInstitutions } from '../../../../api/institution/fetchInstitutions';
import { fetchProjects } from '../../../../api/projects';
import { useCustomNavigate } from '../../../../hooks';
import { Company, Project } from '../../../../types';
import { removeDuplicates, splitAtFirstSpace } from '../../../../utils';
import { SearchSuggestions } from './SearchSuggestions';

type SearchProps = {
    onMenuClick: () => void;
};

enum SuggestionType {
    Company,
    Project,
    PastSearch
}

const Search = ({ onMenuClick }: SearchProps) => {
    const { navigateToCompanyDetails, navigateToProjectDetails } = useCustomNavigate();
    const [projectMatches, setProjectMatches] = useState<Project[]>([]);
    const [companyMatches, setCompanyMatches] = useState<Company[]>([]);

    const debouncedFetchSuggestions = useMemo(
        () =>
            // using debounce: 600 ms waiting time after last keypress, before fetching suggestions
            debounce(async (val: string) => {
                // when one or more whitespaces inside searchterm, we split at the first space and search for both strings
                const searchterms = splitAtFirstSpace(val.trim());

                await Promise.all(
                    searchterms.map(
                        async (val) =>
                            await fetchProjects(
                                createProjectSearchOptions(val, { start: 0, limit: 3 })
                            )
                    )
                )
                    .then((projectsResponse) =>
                        removeDuplicates(projectsResponse.flatMap(({ projects }) => projects))
                    )
                    .then((projects) => setProjectMatches(projects));

                await Promise.all(
                    searchterms.map(
                        async (val) =>
                            await fetchInstitutions(
                                createCompanySearchTermQueryOptions(val, { start: 0, limit: 3 })
                            )
                    )
                )
                    .then((companiesResponse) =>
                        removeDuplicates(
                            companiesResponse.flatMap(
                                ({ institutions }) => institutions as Company[]
                            )
                        )
                    )
                    .then((companies) => setCompanyMatches(companies));
            }, 600),
        []
    );

    const goToDirectly = (type: SuggestionType, id: string) => {
        if (type === SuggestionType.Project) navigateToProjectDetails(id);
        if (type === SuggestionType.Company) navigateToCompanyDetails(id);
    };

    const handleClearMatches = useCallback(() => {
        setCompanyMatches([]);
        setProjectMatches([]);
    }, []);

    return (
        <SearchSuggestions
            onMenuClick={onMenuClick}
            goToDirectly={goToDirectly}
            clearMatches={handleClearMatches}
            fetchSuggestions={debouncedFetchSuggestions}
            companyMatches={companyMatches}
            projectMatches={projectMatches}
        />
    );
};

export { Search, SuggestionType };
